<template>
  <el-card class="campaign-group-card" shadow="hover">
    <template #header>
      <div class="card-header">
        <div class="header-left">
          <span class="group-name">{{ group.attributes.name }}</span>
          <el-tag size="small" type="info" class="campaigns-count">
            {{ group.attributes.campaigns_count }} Campaigns
          </el-tag>
        </div>
        <div class="header-right">
          <el-dropdown trigger="click" @command="handleCommand">
            <el-button type="text" class="more-btn">
              <el-icon><MoreFilled /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="edit">Edit</el-dropdown-item>
                <el-dropdown-item command="delete" divided
                  >Delete</el-dropdown-item
                >
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </template>

    <div class="card-body">
      <p class="aggregated-label">Aggregated stat for the period</p>

      <div class="stats-row">
        <div class="stat-item">
          <div class="stat-label">IMPRESSIONS</div>
          <div class="stat-value">
            {{ formatNumber(groupStats.impressions) }}
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-label">REVENUE</div>
          <div class="stat-value revenue">
            ${{ formatNumber(groupStats.revenue) }}
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-label">CONVERSIONS</div>
          <div class="stat-value">
            {{ formatNumber(groupStats.conversions) }}
          </div>
        </div>
      </div>

      <div class="stats-row secondary">
        <div class="stat-item">
          <div class="stat-label">CPM</div>
          <div class="stat-value">${{ formatNumber(groupStats.cpm, 4) }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">DAILY REVENUE</div>
          <div class="stat-value">
            ${{ formatNumber(groupStats.daily_revenue) }}
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-label">CPA</div>
          <div class="stat-value">${{ formatNumber(groupStats.cpa, 4) }}</div>
        </div>
      </div>

      <div class="chart-placeholder">
        <div class="chart-label">Performance (Revenue)</div>
        <div class="mini-chart">
          <svg viewBox="0 0 200 50" class="chart-svg">
            <polyline
              :points="chartPoints"
              fill="none"
              stroke="#6366f1"
              stroke-width="2"
            />
          </svg>
        </div>
      </div>

      <div class="card-footer">
        <span class="footer-stats">
          {{ formatNumber(groupStats.impressions) }} imps /
          {{ groupStats.conversions }} conv
        </span>
        <el-button type="primary" link @click="downloadCsv">
          Download CSV >
        </el-button>
      </div>
    </div>
  </el-card>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue'
import { MoreFilled } from '@element-plus/icons-vue'
import {
  ICampaignGroup,
  ICampaignGroupStats
} from '@/apps/traforama/interfaces/campaignGroups'

export default defineComponent({
  name: 'CampaignGroupCard',
  components: {
    MoreFilled
  },
  props: {
    group: {
      type: Object as PropType<ICampaignGroup>,
      required: true
    },
    stats: {
      type: Object as PropType<ICampaignGroupStats>,
      default: () => ({
        impressions: 0,
        revenue: 0,
        conversions: 0,
        cpm: 0,
        daily_revenue: 0,
        cpa: 0
      })
    }
  },
  emits: ['edit', 'delete', 'download-csv'],
  setup(props, { emit }) {
    const groupStats = computed(
      () =>
        props.stats || {
          impressions: 0,
          revenue: 0,
          conversions: 0,
          cpm: 0,
          daily_revenue: 0,
          cpa: 0
        }
    )

    const chartPoints = computed(() => {
      const chartData = groupStats.value.chart_data || []
      if (chartData.length === 0) {
        return '0,50 200,50' // Flat line if no data
      }

      const revenues = chartData.map((d) => d.revenue)
      const maxRevenue = Math.max(...revenues, 1) // Avoid division by zero
      const width = 200
      const maxY = 50
      const step = width / Math.max(chartData.length - 1, 1)

      return chartData
        .map((d, idx) => {
          const y = maxY - (d.revenue / maxRevenue) * (maxY - 5)
          return `${idx * step},${y}`
        })
        .join(' ')
    })

    const formatNumber = (num: number, decimals = 2) => {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(decimals) + 'M'
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(decimals) + 'K'
      }
      return num?.toFixed(decimals) || '0.' + '0'.repeat(decimals)
    }

    const handleCommand = (command: string) => {
      if (command === 'edit') {
        emit('edit', props.group)
      } else if (command === 'delete') {
        emit('delete', props.group)
      }
    }

    const downloadCsv = () => {
      emit('download-csv', props.group)
    }

    return {
      groupStats,
      chartPoints,
      formatNumber,
      handleCommand,
      downloadCsv
    }
  }
})
</script>

<style lang="scss" scoped>
.campaign-group-card {
  width: 320px;
  margin: 10px;
  border-radius: 8px;

  :deep(.el-card__header) {
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
  }

  :deep(.el-card__body) {
    padding: 16px;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.group-name {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}

.campaigns-count {
  font-size: 12px;
}

.more-btn {
  padding: 4px;
  color: #909399;

  &:hover {
    color: #409eff;
  }
}

.aggregated-label {
  font-size: 12px;
  color: #909399;
  margin: 0 0 12px 0;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;

  &.secondary {
    margin-bottom: 20px;
  }
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-label {
  font-size: 11px;
  color: #909399;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;

  &.spend {
    color: #303133;
  }

  &.revenue {
    color: #67c23a;
  }
}

.chart-placeholder {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

.chart-label {
  font-size: 11px;
  color: #909399;
  margin-bottom: 8px;
}

.mini-chart {
  height: 50px;
}

.chart-svg {
  width: 100%;
  height: 100%;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.footer-stats {
  font-size: 12px;
  color: #909399;
}
</style>
